import React, { useState, useEffect } from 'react';
import { Paper, Popover, ClickAwayListener } from '@material-ui/core';
import DateRangeInput from './components/DateRangeInput';
import PresetPanel from './components/PresetPanel';
import Calendar from './components/Calendar';
import { useDateRange } from './hooks/useDateRange';
import { useStyles } from './styles';

/**
 * Компонент DateRangePicker
 *
 * @param {Object} props
 * @param {Date} props.initialStartDate - Початкова дата діапазону
 * @param {Date} props.initialEndDate - Кінцева дата діапазону
 * @param {Function} props.onChange - Колбек при зміні діапазону (startDate, endDate)
 * @param {Boolean} props.disableFuture - Вимкнути майбутні дати
 * @param {String} props.placeholder - Placeholder для input
 */
const DateRangePicker = ({
    initialStartDate = null,
    initialEndDate = null,
    onChange,
    disableFuture = false,
    placeholder = "Оберіть діапазон дат"
}) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const {
        startDate,
        endDate,
        currentMonth,
        setHoverDate,
        handleDateClick,
        setDateRange,
        isInRange,
        isRangeStart,
        isRangeEnd,
        goToNextMonth,
        goToPreviousMonth
    } = useDateRange(initialStartDate, initialEndDate);

    useEffect(() => {
        if (onChange && startDate && endDate) {
            onChange(startDate, endDate);
        }
    }, [startDate, endDate, onChange]);

    const handleInputClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePresetClick = (preset) => {
        const range = preset.getRange();
        setDateRange(range.startDate, range.endDate);

        if (range.startDate && range.endDate) {
            handleClose();
        }
    };

    const handleDateClickInternal = (date) => {
        handleDateClick(date);

        // Закрити picker коли обрано обидві дати
        // Перевіряємо чи це другий клік (тобто вже є startDate але немає endDate)
        if (startDate && !endDate) {
            // Затримка для анімації перед закриттям
            setTimeout(() => {
                handleClose();
            }, 200);
        }
    };

    const open = Boolean(anchorEl);

    return (
        <div>
            <DateRangeInput
                startDate={startDate}
                endDate={endDate}
                onClick={handleInputClick}
                placeholder={placeholder}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    className: classes.paper
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper className={classes.paper} elevation={0}>
                        <div className={classes.container}>
                            <PresetPanel
                                startDate={startDate}
                                endDate={endDate}
                                onPresetClick={handlePresetClick}
                            />

                            <Calendar
                                currentMonth={currentMonth}
                                onPreviousMonth={goToPreviousMonth}
                                onNextMonth={goToNextMonth}
                                onDateClick={handleDateClickInternal}
                                onDateHover={setHoverDate}
                                isInRange={isInRange}
                                isRangeStart={isRangeStart}
                                isRangeEnd={isRangeEnd}
                                disableFuture={disableFuture}
                            />
                        </div>
                    </Paper>
                </ClickAwayListener>
            </Popover>
        </div>
    );
};

export default DateRangePicker;
